import { useCallback, useMemo } from 'react';

import {
  DEFAULT_PAGINATION_PAGE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
} from '@shared/constants/pagination.constants';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';

import {
  clampUiPage,
  getDashboardPaginationFromSearchParams,
  getDashboardRootIdsFromSearchParams,
  normalizeDashboardRootIds,
} from '../helpers/dashboardScope';
import { MAX_DASHBOARD_PAGE_SIZE, DASHBOARD_SEGMENT_PARAM, DASHBOARD_ROOT_IDS_PARAM } from '../dashboard.constants';
import type { EDashboardSegmentKey } from '../types/dashboard.types';

export const useDashboardUrlState = () => {
  const { searchParams, changeMany } = useSearchParams();
  const selectedRootIds = useMemo(() => getDashboardRootIdsFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getDashboardPaginationFromSearchParams(searchParams), [searchParams]);

  const setSelectedRootIds = useCallback(
    (rootIds: string[]) =>
      changeMany({
        [DASHBOARD_ROOT_IDS_PARAM]: normalizeDashboardRootIds(rootIds),
        [PAGINATION_PAGE_PARAM]: String(DEFAULT_PAGINATION_PAGE),
      }),
    [changeMany]
  );
  const setSelectedSegmentKey = useCallback(
    (segmentKey: EDashboardSegmentKey) =>
      changeMany(
        { [DASHBOARD_SEGMENT_PARAM]: segmentKey, [PAGINATION_PAGE_PARAM]: String(DEFAULT_PAGINATION_PAGE) },
        { replace: false }
      ),
    [changeMany]
  );
  const setPage = useCallback(
    (page: number) =>
      changeMany(
        {
          [PAGINATION_PAGE_PARAM]: String(clampUiPage(page)),
        },
        { replace: false }
      ),
    [changeMany]
  );
  const replacePage = useCallback(
    (page: number) =>
      changeMany({
        [PAGINATION_PAGE_PARAM]: String(clampUiPage(page)),
      }),
    [changeMany]
  );
  const setPageSize = useCallback(
    (pageSize: number) =>
      changeMany(
        {
          [PAGINATION_PAGE_SIZE_PARAM]: String(Math.min(Math.max(pageSize, 1), MAX_DASHBOARD_PAGE_SIZE)),
          [PAGINATION_PAGE_PARAM]: String(DEFAULT_PAGINATION_PAGE),
        },
        { replace: false }
      ),
    [changeMany]
  );

  return {
    selectedRootIds,
    ...pagination,
    setSelectedRootIds,
    setSelectedSegmentKey,
    setPage,
    replacePage,
    setPageSize,
  };
};
